import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { HERO_PHOTOS } from "@/data/content";

const SLIDE_MS = 4500;

export default function Hero({ onJoin }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setActive((a) => (a + 1) % HERO_PHOTOS.length),
      SLIDE_MS
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex flex-col">
      <div className="absolute inset-0" data-testid="hero-slideshow">
        {HERO_PHOTOS.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            aria-hidden={i !== active}
            loading={i === 0 ? "eager" : "lazy"}
            data-testid={`hero-slide-${i + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/70" />
      </div>

      <div className="relative flex-1 flex items-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-16">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold text-[#FF5A1F] mb-5"
          >
            Premium Fitness Club
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-wide text-white uppercase"
          >
            Transform Your Body.
            <br />
            <span className="text-[#FF5A1F]">Elevate Your Life.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-[#D1D1D1] max-w-xl"
          >
            Your fitness journey starts here. Train stronger, live healthier, and become your best
            self.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#branches"
              data-testid="hero-explore-gyms-button"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/70 hover:border-[#FF5A1F] hover:text-[#FF5A1F] text-white font-heading text-xl tracking-widest px-8 py-3.5 rounded-sm transition-colors"
            >
              EXPLORE OUR GYMS
              <ArrowRight className="w-5 h-5" />
            </a>
            <button
              data-testid="hero-join-now-button"
              onClick={onJoin}
              className="inline-flex items-center justify-center bg-[#FF5A1F] hover:bg-[#E0480F] text-white font-heading text-xl tracking-widest px-10 py-3.5 rounded-sm transition-colors shadow-[0_0_30px_rgba(255,90,31,0.4)]"
            >
              JOIN NOW
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="relative"
      >
        <div
          data-testid="hero-slide-dots"
          className="absolute -top-10 right-4 sm:right-6 lg:right-8 flex items-center gap-1.5"
        >
          {HERO_PHOTOS.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-slide-dot-${i + 1}`}
              onClick={() => setActive(i)}
              aria-label={`Show gym photo ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === active ? "w-6 bg-[#FF5A1F]" : "w-1.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
        <div
          data-testid="hero-branch-ticker"
          className="border-t border-[#FF5A1F]/30 bg-[#0A0A0A]/80 backdrop-blur-sm"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
            <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-white">
              <MapPin className="w-4 h-4 text-[#FF5A1F]" /> 2 Branches Now Open
            </span>
            <span className="hidden sm:inline text-[#FF5A1F] font-bold">/</span>
            <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#FF5A1F]">
              1 New Branch Coming Soon
            </span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
